/**
 * Text-to-speech: sentence segmentation (pure, testable without the Web
 * Speech API) plus a small state machine that drives an injected synth. See
 * docs/m4-plan.md F5 — explicitly a stretch goal, EPUB-only, browser
 * `SpeechSynthesis` only (no cloud voices, no audio export).
 */
/**
 * Split flattened block text into sentences. Uses `Intl.Segmenter` (sentence
 * granularity) where available; falls back to a punctuation-based regex.
 */
export function segmentSentences(text) {
    if (!text.trim())
        return [];
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
        const segmenter = new Intl.Segmenter(undefined, { granularity: 'sentence' });
        const spans = [];
        for (const { segment, index } of segmenter.segment(text)) {
            const trimmed = segment.trim();
            if (trimmed)
                spans.push({ start: index, end: index + segment.length, text: trimmed });
        }
        return spans;
    }
    const spans = [];
    const re = /[^.!?]+[.!?]+(?:\s+|$)/g;
    let m;
    let last = 0;
    while ((m = re.exec(text))) {
        const trimmed = m[0].trim();
        if (trimmed)
            spans.push({ start: m.index, end: m.index + m[0].length, text: trimmed });
        last = re.lastIndex;
    }
    if (last < text.length) {
        const rest = text.slice(last);
        const trimmed = rest.trim();
        if (trimmed)
            spans.push({ start: last + rest.indexOf(trimmed), end: text.length, text: trimmed });
    }
    return spans;
}
export function createTtsController(opts) {
    let sentences = [];
    let index = -1;
    let playing = false;
    let rate = 1;
    let voice = null;
    let generation = 0; // bumped on stop()/a fresh play() so a stray async advanceSpine can't resurrect a dead run
    const emit = () => opts.onStateChange(getState());
    const getState = () => ({ playing, sentence: sentences[index] ?? null, rate, voice });
    const speakIndex = (gen) => {
        if (gen !== generation)
            return;
        const sentence = sentences[index];
        if (!sentence) {
            void opts.advanceSpine().then((advanced) => {
                if (gen !== generation)
                    return;
                if (!advanced) {
                    playing = false;
                    opts.onSentence(null);
                    emit();
                    return;
                }
                sentences = opts.getSentences();
                index = 0;
                speakIndex(gen);
            });
            return;
        }
        opts.onSentence(sentence);
        const utterance = opts.createUtterance(sentence.text);
        utterance.rate = rate;
        utterance.voice = voice;
        utterance.onend = () => {
            if (gen !== generation || !playing)
                return;
            index++;
            speakIndex(gen);
        };
        opts.synth.speak(utterance);
        emit();
    };
    return {
        play() {
            generation++;
            sentences = opts.getSentences();
            index = Math.max(index, 0);
            playing = true;
            speakIndex(generation);
        },
        pause() {
            opts.synth.pause();
            playing = false;
            emit();
        },
        resume() {
            opts.synth.resume();
            playing = true;
            emit();
        },
        stop() {
            generation++;
            opts.synth.cancel();
            playing = false;
            index = -1;
            sentences = [];
            opts.onSentence(null);
            emit();
        },
        setRate(r) {
            rate = r;
        },
        setVoice(v) {
            voice = v;
        },
        listVoices() {
            return opts.synth.getVoices();
        },
        getState,
    };
}
//# sourceMappingURL=tts.js.map